import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Trash2, Plus } from "lucide-react";

const HERO = "https://images.pexels.com/photos/1192031/pexels-photo-1192031.jpeg";

export default function FuelPage() {
  const [day, setDay] = useState(null);
  const [swaps, setSwaps] = useState([]);
  const [form, setForm] = useState({ name: "", calories: "", protein: "", carbs: "", fat: "", meal: "breakfast" });
  const [genOpen, setGenOpen] = useState(false);
  const [goal, setGoal] = useState("high-protein dinner");
  const [recipe, setRecipe] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [d, s] = await Promise.all([api.get("/fuel"), api.get("/fuel/swaps")]);
    setDay(d.data);
    setSwaps(s.data.swaps);
  };
  useEffect(() => { load(); }, []);

  const addEntry = async (e) => {
    e.preventDefault();
    await api.post("/fuel", {
      name: form.name,
      calories: parseFloat(form.calories) || 0,
      protein: parseFloat(form.protein) || 0,
      carbs: parseFloat(form.carbs) || 0,
      fat: parseFloat(form.fat) || 0,
      meal: form.meal,
    });
    setForm({ name: "", calories: "", protein: "", carbs: "", fat: "", meal: "breakfast" });
    await load();
  };

  const deleteEntry = async (id) => { await api.delete(`/fuel/${id}`); await load(); };

  const generate = async () => {
    setBusy(true);
    setRecipe(null);
    try {
      const { data } = await api.post("/recipes/generate", { goal });
      setRecipe(data);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!day) return <div className="p-10 text-zinc-500">Loading…</div>;

  return (
    <div className="fade-up">
      <section
        className="mb-8 concrete"
        style={{
          minHeight: 180,
          border: "1px solid #27272A",
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.9) 100%), url(${HERO})`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 p-6 md:p-10">
          <p className="eyebrow" style={{ color: "#FF0055" }}>Fuel the rebuild</p>
          <h1 className="font-display font-black text-3xl md:text-5xl mt-2 leading-[0.95]">
            REAL FOOD.<br/>NO BANS.
          </h1>
        </div>
      </section>

      {/* Macro totals */}
      <div className="grid grid-cols-4 gap-2 md:gap-3 mb-6 stagger">
        {[
          { k: "calories", label: "Kcal", accent: true },
          { k: "protein", label: "Protein" },
          { k: "carbs", label: "Carbs" },
          { k: "fat", label: "Fat" },
        ].map((m) => (
          <div className="card" key={m.k}>
            <p className="eyebrow">{m.label}</p>
            <p className="font-display font-black text-2xl md:text-3xl mt-2" style={{ color: m.accent ? "#FF0055" : "white" }}>
              {Math.round(day.totals[m.k])}
            </p>
          </div>
        ))}
      </div>

      {/* Add entry */}
      <form onSubmit={addEntry} className="card mb-8 grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="fuel-form">
        <div className="col-span-2 md:col-span-2">
          <label className="label">Food</label>
          <input className="input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Greek yogurt bowl" required data-testid="fuel-name"/>
        </div>
        <div>
          <label className="label">Kcal</label>
          <input className="input" type="number" value={form.calories} onChange={(e) => setForm({...form, calories: e.target.value})} required data-testid="fuel-cal"/>
        </div>
        <div>
          <label className="label">Protein</label>
          <input className="input" type="number" value={form.protein} onChange={(e) => setForm({...form, protein: e.target.value})}/>
        </div>
        <div>
          <label className="label">Carbs</label>
          <input className="input" type="number" value={form.carbs} onChange={(e) => setForm({...form, carbs: e.target.value})}/>
        </div>
        <div>
          <label className="label">Fat</label>
          <input className="input" type="number" value={form.fat} onChange={(e) => setForm({...form, fat: e.target.value})}/>
        </div>
        <div className="col-span-2 md:col-span-1">
          <label className="label">Meal</label>
          <select className="input" value={form.meal} onChange={(e) => setForm({...form, meal: e.target.value})}>
            <option value="breakfast">Breakfast</option>
            <option value="lunch">Lunch</option>
            <option value="dinner">Dinner</option>
            <option value="snack">Snack</option>
          </select>
        </div>
        <div className="col-span-2 md:col-span-5 flex items-end">
          <button className="btn-primary flex items-center gap-2" data-testid="add-fuel"><Plus size={14}/> Log meal</button>
        </div>
      </form>

      {/* Entries */}
      <section className="mb-10">
        <p className="eyebrow mb-3">Today's meals</p>
        {day.entries.length === 0 ? (
          <div className="card text-zinc-500 text-sm">Nothing logged yet. Start with your next meal.</div>
        ) : (
          <div className="space-y-2">
            {day.entries.map((e) => (
              <div key={e.id} className="card flex items-center justify-between" data-testid={`fuel-entry-${e.id}`}>
                <div>
                  <p className="font-display font-bold uppercase">{e.name}</p>
                  <p className="text-xs text-zinc-500 mt-1 uppercase tracking-widest">
                    {e.meal} · {Math.round(e.calories)} kcal · P {e.protein} · C {e.carbs} · F {e.fat}
                  </p>
                </div>
                <button className="btn-ghost" onClick={() => deleteEntry(e.id)} data-testid={`del-${e.id}`}><Trash2 size={16}/></button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* AI Recipe */}
      <section className="card mb-10" data-testid="recipe-section">
        <p className="eyebrow" style={{ color: "#FF0055" }}>AI recipe · CINDR fuel</p>
        <h2 className="font-display font-black text-2xl md:text-3xl mt-2 uppercase">Build a meal in seconds</h2>
        <div className="mt-4 flex gap-3 flex-wrap">
          <input className="input flex-1 min-w-[240px]" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="e.g. high-protein breakfast under 500 kcal" data-testid="recipe-goal"/>
          <button className="btn-primary flex items-center gap-2" onClick={generate} disabled={busy} data-testid="gen-recipe">
            <Sparkles size={14}/> {busy ? "Cooking…" : "Generate"}
          </button>
        </div>
        {recipe && (
          <div className="mt-6 pt-6" style={{ borderTop: "1px solid #27272A" }}>
            <p className="eyebrow" style={{ color: "#FF0055" }}>{recipe.tagline}</p>
            <h3 className="font-display font-black text-xl md:text-2xl mt-2 uppercase">{recipe.title}</h3>
            <p className="text-xs text-zinc-500 mt-2 uppercase tracking-widest">
              {recipe.time_min} min · {recipe.servings} servings · {recipe.macros_per_serving?.calories} kcal · P {recipe.macros_per_serving?.protein}
            </p>
            <div className="grid md:grid-cols-2 gap-6 mt-5">
              <div>
                <p className="eyebrow mb-2">Ingredients</p>
                <ul className="text-sm text-zinc-300 space-y-1 list-disc pl-5">
                  {recipe.ingredients?.map((i, k) => <li key={k}>{i}</li>)}
                </ul>
              </div>
              <div>
                <p className="eyebrow mb-2">Steps</p>
                <ol className="text-sm text-zinc-300 space-y-2 list-decimal pl-5">
                  {recipe.steps?.map((s, k) => <li key={k}>{s}</li>)}
                </ol>
              </div>
            </div>
            {recipe.cindr_tip && (
              <p className="mt-6 text-sm italic" style={{ color: "#FF0055" }}>“{recipe.cindr_tip}”</p>
            )}
          </div>
        )}
      </section>

      {/* Better-choice swaps */}
      <section>
        <p className="eyebrow mb-3">Better-choice swaps</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {swaps.map((s, i) => (
            <div className="card" key={i}>
              <p className="text-xs text-zinc-500 uppercase tracking-widest">Instead of</p>
              <p className="font-display font-bold uppercase mt-1">{s.instead_of}</p>
              <p className="text-xs mt-3" style={{ color: "#FF0055" }}>TRY</p>
              <p className="text-sm text-zinc-200 mt-1">{s.try}</p>
              <p className="text-xs text-zinc-500 mt-3">{s.why}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
